/**
 * Eventastic Calendar Block v2.2 (Migrated to /events-expanded API)
 *
 * This script handles the frontend functionality for the Eventastic Calendar block,
 * now using the /events-expanded endpoint for better recurring event support.
 */

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================
function parseYMDString(dateString) {
    if (!dateString || typeof dateString !== 'string' || dateString.length !== 8) return null;
    const year = parseInt(dateString.substring(0, 4), 10);
    const month = parseInt(dateString.substring(4, 6), 10) - 1;
    const day = parseInt(dateString.substring(6, 8), 10);
    return new Date(year, month, day);
}

function formatDate({ input, dateFormat = "Y-M-D" }) {
    const date = (input instanceof Date) ? input : new Date(input);
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const shortMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const year = date.getFullYear();
    const monthIndex = date.getMonth();
    const day = date.getDate();
    const zeroMonth = ('0' + (monthIndex + 1)).slice(-2);
    const zeroDay = ('0' + day).slice(-2);
    switch (dateFormat) {
        case "Y-M-D": return `${year}-${zeroMonth}-${zeroDay}`;
        case "Ymd": return `${year}${zeroMonth}${zeroDay}`;
        case "Y-M": return `${year}-${zeroMonth}`;
        case "M D": return `${months[monthIndex]} ${day}`;
        case "m D": return `${shortMonths[monthIndex]} ${day}`;
        case "monthName": return months[monthIndex];
        case "fullDate": return `${months[monthIndex]} ${day}, ${year}`;
        default: return `${year}-${zeroMonth}-${zeroDay}`;
    }
}

/**
 * Strip invisible chars; coerce REST/ACF shapes to a single string for checks.
 */
function normalizeEventStartTimeString(raw) {
    if (raw == null || raw === false) return '';
    if (Array.isArray(raw)) raw = raw[0];
    if (typeof raw === 'number') return Number.isFinite(raw) ? String(raw) : '';
    if (typeof raw !== 'string') return '';
    return raw.replace(/[\uFEFF\u200B-\u200D\u2060]/g, '').trim();
}

/**
 * REST payloads may put the field on meta or omit acf; WP sometimes returns a single-element array.
 */
function resolveEventStartTimeFromPayload(event) {
    let raw =
        event?.acf?.event_start_time ??
        event?.meta?.event_start_time ??
        event?.event_start_time;
    if (raw === undefined || raw === null || raw === false) return '';
    if (Array.isArray(raw)) raw = raw[0];
    return raw;
}

/**
 * True only when a real clock time was saved — not empty, not 00:00:00 (ACF H:i:s default),
 * and not 12:00 am (display-format midnight default from some APIs).
 */
function hasMeaningfulEventStartTime(raw) {
    const s = normalizeEventStartTimeString(raw);
    if (!s) return false;

    const compact = s.replace(/\s+/g, ' ').trim();

    // 12-hour midnight (ACF display_format "g:i a" or similar defaults)
    if (/^12\s*:\s*0{1,2}(?:\s*:\s*0{1,2})?\s*a\.?m\.?$/i.test(compact)) {
        return false;
    }

    if (/\b(a|p)\.?m\.?\b/i.test(compact)) {
        const t = parseTime(compact);
        if (!t) return false;
        return t.getHours() !== 0 || t.getMinutes() !== 0 || t.getSeconds() !== 0;
    }

    // 24-hour H:i:s at start, or after a "T" / space (ISO-ish fragments)
    let m = compact.match(/^(\d{1,2})\s*:\s*(\d{2})(?:\s*:\s*(\d{2}))?/);
    if (!m) {
        m = compact.match(/(?:^|[T\s])(\d{1,2})\s*:\s*(\d{2})(?:\s*:\s*(\d{2}))?(?!\d)/i);
    }
    if (!m) return true;

    const h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    const sec = m[3] != null ? parseInt(m[3], 10) : 0;
    if ([h, min, sec].some((n) => Number.isNaN(n))) return true;
    return !(h === 0 && min === 0 && sec === 0);
}

/**
 * If Intl (or the API) still yields midnight, hide it (last line of defense).
 */
function isFormattedMidnightAm(displayStr) {
    if (!displayStr || typeof displayStr !== 'string') return false;
    const t = displayStr.replace(/[\u202F\u00A0]/g, ' ').trim();
    return /12:00(\s*:00)?\s*AM/i.test(t) && !/PM/i.test(t);
}

function parseTime(timeStr) {
    if (typeof timeStr !== 'string' || timeStr.trim() === '') return false;
    // Handle both 12-hour (e.g., "4:30 pm") and 24-hour (e.g., "16:30:00") formats
    const is12Hour = /am|pm/i.test(timeStr);
    if (is12Hour) {
        const [time, period] = timeStr.toLowerCase().split(' ');
        const [hours, minutes] = time.split(':');
        let h = parseInt(hours, 10);
        if (period === 'pm' && h !== 12) h += 12;
        if (period === 'am' && h === 12) h = 0;
        const date = new Date();
        date.setHours(h, parseInt(minutes, 10), 0, 0);
        return date;
    } else {
        const [hours, minutes] = timeStr.split(':');
        const date = new Date();
        date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
        return date;
    }
}


// =============================================================================
// EVENT DATA PROCESSING
// =============================================================================

/**
 * NEW: Transforms events from the /events-expanded API into the format required by FullCalendar.
 * The new API returns pre-expanded event instances, so no date looping is needed here.
 * @param {Array} eventObjects - The array of event objects from the /events-expanded API.
 * @returns {Array} An array of event objects formatted for FullCalendar.
 */
function transformExpandedEvents(eventObjects) {
    if (!Array.isArray(eventObjects)) return [];

    return eventObjects.map(event => {
        const startDate = parseYMDString(event.acf.event_start_date);
        if (!startDate) return null; // Skip if the date is invalid

        const resolvedStartTime = normalizeEventStartTimeString(resolveEventStartTimeFromPayload(event));
        const hasTime = hasMeaningfulEventStartTime(resolvedStartTime);
        const eventStartTimeForUi = hasTime ? resolvedStartTime : '';

        // Reconstruct the event object to match the structure expected by buildEventsGrid
        return {
            id: event.id, // The unique instance ID, e.g., "123_20251016"
            title: event.title,
            start: startDate,
            end: null, // Expanded events are single-day instances
            allDay: !hasTime,
            extendedProps: {
                id: event.id.toString().split('_')[0], // The original post ID
                permalink: event.link,
                featured_image: event.kraken.event_featured_img,
                title: { rendered: event.title },
                // NOTE: The /events-expanded API must be modified to return this category data.
                categories: event.categories || [],
                // Map the new 'acf' object to the old 'events_meta' structure for compatibility
                events_meta: {
                    event_start_date: event.acf.event_start_date,
                    event_end_date: event.acf.event_end_date,
                    event_start_time: eventStartTimeForUi,
                    event_end_time: event.acf.event_end_time,
                    events_addr_multi: event.acf.events_addr_multi,
                    events_event_all_day: String(!hasTime),
                    // Add other fields from 'acf' as needed to maintain compatibility
                }
            }
        };
    }).filter(Boolean); // Filter out any null entries from failed date parsing
}


// =============================================================================
// DOM & UI FUNCTIONS
// =============================================================================
function buildEventsGrid(events, args = {}) {
    const $target = jQuery('#calendarList');
    const config = jQuery('#calendar-container').data();
    const maxEvents = config.maxnumberofgrideventstoshow || 10;
    let output = "";
    events.sort((a, b) => {
        if (a.start < b.start) return -1;
        if (a.start > b.start) return 1;
        const timeA = hasMeaningfulEventStartTime(a.extendedProps.events_meta.event_start_time)
            ? parseTime(a.extendedProps.events_meta.event_start_time)
            : false;
        const timeB = hasMeaningfulEventStartTime(b.extendedProps.events_meta.event_start_time)
            ? parseTime(b.extendedProps.events_meta.event_start_time)
            : false;
        if (!timeA || !timeB) return 0;
        return timeA - timeB;
    });
    if (events.length > 0) {
        events.forEach((event, index) => {
            const props = event.extendedProps;
            const dateToDisplay = args.clickedDate ? args.clickedDate : event.start;
            const eventDate = formatDate({ input: dateToDisplay, dateFormat: config.cardconfig_dateformat || 'M D' });
            let eventTime = '';
            const rawStartTime = props.events_meta.event_start_time;
            if (hasMeaningfulEventStartTime(rawStartTime)) {
                eventTime = rawStartTime;
                if (!/am|pm/i.test(eventTime)) {
                    const dateObj = new Date(`1970-01-01T${eventTime}`);
                    if (!isNaN(dateObj.getTime())) {
                        eventTime = new Intl.DateTimeFormat('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                        }).format(dateObj).replace(' ', ''); // Keep format tight
                    }
                }
            }
            if (isFormattedMidnightAm(eventTime)) {
                eventTime = '';
            }
            // Filter out hidden categories
            const hiddenCategoryIds = [4895, 4916, 4922, 4927, 4924, 4923]; // Add category IDs to hide here
            const filteredCategories = Array.isArray(props.categories) 
                ? props.categories.filter(c => !hiddenCategoryIds.includes(c.term_id))
                : [];
            const categories = filteredCategories.map(c => c.name).join(', ');
                
            const location = props.events_meta.events_addr_multi || '';
            const template = `
                <div class="events-card ${index >= maxEvents ? 'overflow-card' : ''}">
                    <a href="${props.permalink}" target="_blank">
                        <div class="wrapper">
                            ${config.cardconfig_showthumbnail && props.featured_image ? `<div class="image-wrapper" style="background-image:url(${props.featured_image});"></div>` : ''}
                            <div class="content">
                                ${config.cardconfig_showcategories && categories ? `<div class="categories">${categories}</div>` : ''}
                                <div class="date">${eventDate}${eventTime ? ` ${eventTime}` : ''}</div>
                                <div class="title">${props.title.rendered}</div>
                                ${location ? `<div class="location">${location}</div>` : ''}
                            </div>
                        </div>
                    </a>
                </div>
            `;
            output += template;
        });
        if (events.length > maxEvents) {
            output += "<button id='eventastic-calendar-view-more'>View More</button>";
        }
    } else {
        const mssg = config.contentconfig_failuremessage || "There are no events for this day.";
        output = `<div class='ajax-message'>${mssg}</div>`;
    }
    $target.html(output);
}

function toggleViewMore() {
    const $button = jQuery('#eventastic-calendar-view-more');
    $button.toggleClass('active');
    jQuery('.overflow-card').toggleClass('active');
    $button.text($button.hasClass('active') ? 'Show Less' : 'View More');
}

// =============================================================================
// FILTERING & DATA RETRIEVAL
// =============================================================================
function getActiveCategoryIds() {
    const $activeButtons = jQuery(".event-category-filter.button.active");

    if ($activeButtons.length > 0) {
        return $activeButtons.map((_, el) => jQuery(el).data('category')).get();
    } else {
        const config = jQuery('#calendar-container').data();
        return config.contentconfig_categories || [];
    }
}


/**
 * MODIFIED: Fetches events from the new /events-expanded endpoint.
 * This endpoint returns pre-expanded events, simplifying the frontend logic.
 * Now includes per_page=-1 to fetch ALL events for the month.
 */
async function fetchEventsForMonth(date, calendar, loadedMonths) {
    const categoryIds = getActiveCategoryIds();
    const targetMonth = formatDate({ input: date, dateFormat: 'Y-M' });
    const cacheKey = `${targetMonth}_${categoryIds.join(',')}`;

    if (loadedMonths.includes(cacheKey)) return;

    const $loader = jQuery('.eventastic-sidebar .eventastic-loader');
    try {
        $loader.addClass('is-loading');

        const startDate = formatDate({ input: new Date(date.getFullYear(), date.getMonth(), 1), dateFormat: 'Y-M-D' });
        const endDate = formatDate({ input: new Date(date.getFullYear(), date.getMonth() + 1, 0), dateFormat: 'Y-M-D' });
        const apiBase = window.preLoadData.rest_url;

        // MODIFIED: Added per_page=-1 to the URL to ensure all events for the month are fetched.
        let apiUrl = `${apiBase}kraken/v1/events-expanded?per_page=-1&date_filter=true&start_date=${startDate}&end_date=${endDate}`;

        if (categoryIds.length > 0) {
            apiUrl += `&event_category=${categoryIds.join(',')}`;
        }

        const response = await fetch(apiUrl);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();

        loadedMonths.push(cacheKey);

        if (data && Array.isArray(data) && data.length > 0) {
            const newEvents = transformExpandedEvents(data);
            calendar.addEventSource(newEvents);
        }
    } catch (error) {
        console.error('Failed to fetch events for month:', error);
    } finally {
        $loader.removeClass('is-loading');
    }
}
function getFilteredEvents({ targetDate } = {}) {
    const allEvents = window.eventasticCalendar.getEvents();

    if (!targetDate) {
        return allEvents;
    }

    const targetDateStr = formatDate({ input: targetDate, dateFormat: 'Y-M-D' });
    return allEvents.filter(event => {
        const eventDateStr = formatDate({ input: event.start, dateFormat: 'Y-M-D' });
        return eventDateStr === targetDateStr;
    });
}


// =============================================================================
// MAIN INITIALIZATION (No changes needed here)
// =============================================================================
export function initializeEventasticCalendarBlock() {
    const $calendarContainer = jQuery('#calendar-container');
    if (!$calendarContainer.length) {
        console.error("Eventastic Error: Missing #calendar-container element.");
        return;
    }

    const loadedMonths = [];
    let isInitialLoad = true;

    const calendarEl = document.getElementById('calendar');
    const eventasticCalendar = new FullCalendar.Calendar(calendarEl, {
        height: 'auto',
        headerToolbar: { start: 'title', center: null, end: 'prev,next' },
        eventDisplay: 'none',
        dayCellDidMount,
        datesSet: (dateInfo) => updateCalendarDisplay(dateInfo.view)
    });

    window.eventasticCalendar = eventasticCalendar;

    async function updateCalendarDisplay(view) {
        await fetchEventsForMonth(view.currentStart, eventasticCalendar, loadedMonths);

        const allEvents = eventasticCalendar.getEvents();
        allEvents.forEach(event => {
            const dateStr = formatDate({ input: event.start, dateFormat: 'Y-M-D' });
            const $dayCell = jQuery(`.fc-daygrid-day[data-date="${dateStr}"]`);
            if ($dayCell.length && !$dayCell.find('.event-dot').length) {
                const newDot = document.createElement('div');
                newDot.className = 'event-dot';
                $dayCell.find('.fc-daygrid-day-frame').append(newDot);
            }
        });

        const monthName = formatDate({ input: view.currentStart, dateFormat: 'monthName' });
        jQuery('#events-list-title').html(`<h3>Upcoming ${monthName} Events</h3>`);

        const viewMonth = view.currentStart.getMonth();
        const viewYear = view.currentStart.getFullYear();
        const eventsForMonth = allEvents.filter(event => {
            const eventDate = event.start;
            return eventDate.getMonth() === viewMonth && eventDate.getFullYear() === viewYear;
        });

        buildEventsGrid(eventsForMonth);

        if (isInitialLoad) {
            isInitialLoad = false;
            const todayStr = formatDate({ input: new Date(), dateFormat: 'Y-M-D' });
            const $todayCell = jQuery(`.fc-daygrid-day[data-date="${todayStr}"]`);

            if ($todayCell.length) {
                $todayCell.trigger('click');
            }
        }
    }

    async function refreshCalendarView() {
        const calendar = window.eventasticCalendar;
        if (!calendar) return;

        calendar.getEventSources().forEach(source => source.remove());
        jQuery('.event-dot').remove();
        loadedMonths.length = 0;

        await updateCalendarDisplay(calendar.view);
    }

    function dayCellDidMount(arg) {
        arg.el.addEventListener('click', () => {
            jQuery('.fc-daygrid-day').removeClass('active');
            arg.el.classList.add('active');
            const eventsForDay = getFilteredEvents({ targetDate: arg.date });
            jQuery('#events-list-title').html(`<h3>Events for ${formatDate({input: arg.date, dateFormat: 'fullDate'})}</h3>`);
            buildEventsGrid(eventsForDay, { clickedDate: arg.date });
        });
    }

    jQuery('.event-category-filter.button').on('click', function (e) {
        e.preventDefault();
        jQuery(this).toggleClass('active');
        refreshCalendarView();
    });

    jQuery(".resetFilters").on('click', (e) => {
        e.preventDefault();
        jQuery('.event-category-filter.button.active').removeClass('active');
        refreshCalendarView();
    });

    jQuery('body').on('click', '#eventastic-calendar-view-more', (e) => {
        e.preventDefault();
        toggleViewMore();
    });

    eventasticCalendar.render();
}